import { useCallback, useMemo, useState } from "react";
import { IUserListItemResponse } from "@miplatform/types";
import { useCalendarContext } from "../../../providers";

export const MAX_ACTIVE_ASSIGNABLE_USERS = 3;

function withActiveAssignableUserAppended(
  prev: string[],
  userId: string
): string[] {
  if (prev.includes(userId)) {
    return prev;
  }
  if (prev.length >= MAX_ACTIVE_ASSIGNABLE_USERS) {
    return prev;
  }
  return [...prev, userId];
}

export function useNHCPriorityScheduler() {
  const { assignableUsers } = useCalendarContext();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [activeAssignableUserIds, setActiveAssignableUserIds] = useState<
    string[]
  >([]);

  const [boardExpandedUserId, setBoardExpandedUserId] = useState<string | null>(
    null
  );

  const assignableUserSummaries = useMemo(
    () =>
      assignableUsers?.map((user) => ({
        id: user.id,
        name: user.name || "",
        availableHours: 0,
        workOrderCount: 0,
      })) ?? [],
    [assignableUsers]
  );

  const expandedUsers = useMemo(
    () =>
      activeAssignableUserIds.reduce<IUserListItemResponse[]>((acc, id) => {
        const user = assignableUsers?.find((u) => u.id === id);
        if (user) acc.push(user);
        return acc;
      }, []),
    [assignableUsers, activeAssignableUserIds]
  );

  const effectiveSelectedUserId = useMemo(() => {
    if (
      selectedUserId == null ||
      activeAssignableUserIds.length === 0 ||
      !activeAssignableUserIds.includes(selectedUserId)
    ) {
      return undefined;
    }
    return selectedUserId;
  }, [selectedUserId, activeAssignableUserIds]);

  const handleAddAssignableUser = useCallback((userId: string) => {
    setActiveAssignableUserIds((prev) =>
      withActiveAssignableUserAppended(prev, userId)
    );
  }, []);

  const handleCloseUserColumn = useCallback(
    (userId: string) => {
      const closingSelectedUser = userId === selectedUserId;

      setActiveAssignableUserIds((prev) => {
        if (!prev.includes(userId)) {
          return prev;
        }
        const idx = prev.indexOf(userId);
        const next = prev.filter((id) => id !== userId);

        if (closingSelectedUser) {
          if (next.length === 0) {
            setSelectedUserId(null);
          } else {
            const nextSelected = idx > 0 ? prev[idx - 1] : next[0];
            if (nextSelected !== undefined) {
              setSelectedUserId(nextSelected);
            }
          }
        }

        return next;
      });
      setBoardExpandedUserId((prev) => (prev === userId ? null : prev));
    },
    [selectedUserId]
  );

  const handleBoardExpandedChange = useCallback(
    (userId: string, expanded: boolean) => {
      setBoardExpandedUserId(expanded ? userId : null);
    },
    []
  );

  const handleUserSelect = useCallback((userId: string) => {
    setActiveAssignableUserIds((prev) => {
      if (prev.includes(userId)) {
        setSelectedUserId(userId);
        return prev;
      }
      const next = withActiveAssignableUserAppended(prev, userId);
      if (next !== prev) {
        setSelectedUserId(userId);
      }
      return next;
    });
  }, []);

  return {
    activeAssignableUserIds,
    assignableUserSummaries,
    boardExpandedUserId,
    effectiveSelectedUserId,
    expandedUsers,
    handleAddAssignableUser,
    handleBoardExpandedChange,
    handleCloseUserColumn,
    handleUserSelect,
  };
}
